// SST V2 reservation status taxonomy:
//   Visible  — DRAFT, CONFIRMED, DELIVERED, EXPIRED, CANCELLED (operator may
//              need to act)
//   Terminal — CLOSED, DELETED                                 (workflow is
//              done, hide)
//
// EXPIRED is intentionally Visible, not Terminal: the deadline has passed but
// the parcel may still be physically inside the locker — the operator needs
// to see it to recover (admin code), extend, or cancel the reservation.
//
// CANCELLED / CANCEL are Visible for exactly the same reason (LOCKER-391): on
// the partner side `CANCELLED` is an ACTIVE status, not a closed one, so the
// parcel is still behind the door and the door still counts the reservation.
// Hiding it painted the door available while a parcel sat in it. Our own cancel
// no longer produces `CANCELLED` at all — it PATCHes straight to `DELETED`,
// which IS terminal — so a `CANCELLED` row reaching the console came from the
// partner and means occupied.
// The partner-side rule itself is stated once, at `sstAdapter.cancelReservation`.
//
// Use a deny-list (terminal set) rather than an allow-list. A future unknown
// status defaults to visible, which is the safe direction for operator UX:
// "show me anything I might need to act on" beats silently hiding new states.
//
// V1/V2 wire mismatch: the IoT publisher broadcasts the V2-format reservation
// as-is (`status: "CLOSED" | "CANCELLED" | "DELETED"`). The DDB-persistence
// path goes through `mapSstv2ToV1Reservation` which shortens `CLOSED → CLOSE`
// and `CANCELLED → CANCEL` before write. So the same logical "closed"
// reservation surfaces with TWO different status strings depending on which
// layer the dashboard reads it from (Redux state via MQTT vs. RTK Query via
// `GetLockerCurrentReservations`). The terminal set must recognize both
// shapes — anything else paints the door "Non disponible" until a refresh
// happens that aligns the in-memory state with the V1 form.
//   (See apps/sl2/src/sl2-partners/src/sstv2/helpers/sstv2ReservationMapper.ts
//   STATUS_V2_TO_V1.)

export const SST2_TERMINAL_STATUSES = [
  // V2 (raw SST wire format — what `publishSstv2IoTEvent` broadcasts)
  "CLOSED",
  "DELETED",
  // V1-shortened (what `mapSstv2ToV1Reservation` persists into DDB and what
  // therefore surfaces on `GetLockerCurrentReservations` reads).
  "CLOSE",
  // NOTE: no CANCELLED / CANCEL here, deliberately — see the header comment.
] as const

// LOCKER-402: the PICKED-UP set — the parcel is out of the locker and the
// customer has it. This is deliberately NOT `SST2_TERMINAL_STATUSES`:
//
//   - the terminal set includes `DELETED`, which means the reservation was
//     withdrawn, not collected;
//   - it deliberately EXCLUDES `CANCEL` / `CANCELLED` (LOCKER-391 — on the
//     partner side those are ACTIVE and the parcel is still behind the door),
//     and a colour rule built on the terminal set would be read as "these two
//     sets are the same thing", which is exactly the confusion LOCKER-391 fixed.
//
// Both wire spellings for the same reason the terminal set carries both: the
// IoT publisher broadcasts raw V2 (`CLOSED`) while the DDB-persistence path goes
// through `mapSstv2ToV1Reservation`, which shortens it to `CLOSE`.
//
// V1's `useReservation` computes the same set as `pickedUpStatuses` and puts it
// FIRST in its colour ternary. The V2 port dropped that branch alone, which is
// how a collected parcel came to render red with an expiration code beside a
// green door.
export const SST2_PICKED_UP_STATUSES = ["CLOSE", "CLOSED"] as const

export function isSst2ReservationPickedUp(
  reservation: { status?: string | null } | null | undefined,
): boolean {
  if (!reservation?.status) return false
  return (SST2_PICKED_UP_STATUSES as readonly string[]).includes(
    reservation.status,
  )
}

export function isSst2ReservationVisible(
  reservation: { status?: string | null } | null | undefined,
): boolean {
  if (!reservation?.status) return false
  return !(SST2_TERMINAL_STATUSES as readonly string[]).includes(reservation.status)
}
